import React from 'react';
import { Video, Users, Presentation, PlaySquare, Cloud, Mic, Camera, MonitorPlay, CheckCircle2, Circle, Power } from 'lucide-react';
import { OperationMode } from '../types/crestron';
interface HomePageProps {
  currentMode: OperationMode;
  onModeSelect: (mode: OperationMode) => void;
}
export function HomePage({
  currentMode,
  onModeSelect
}: HomePageProps) {
  const modes = [{
    id: OperationMode.TownhallRemote,
    label: 'Remote Presenter',
    sub: 'Meet Main • Audience Cam',
    icon: Video,
    details: ['Meet main source', 'Mics muted', 'Audience view'],
    color: 'blue'
  }, {
    id: OperationMode.TownhallLocal,
    label: 'Local Presenter',
    sub: 'Wireless Share • Auto-Track',
    icon: Users,
    details: ['Meet wireless', 'Auto-track cam', 'Mics active'],
    color: 'green'
  }, {
    id: OperationMode.TownhallHybrid,
    label: 'Local + Remote',
    sub: 'Hybrid • Multi-View',
    icon: Presentation,
    details: ['Meet + Local', 'PIP layouts', 'Full routing'],
    color: 'purple'
  }, {
    id: OperationMode.ContentPlayback,
    label: 'Content Playback',
    sub: 'Media Display',
    icon: PlaySquare,
    details: ['Local source', 'Mics muted', 'Simple playback'],
    color: 'amber'
  }, {
    id: OperationMode.MeetStream,
    label: 'Meet Cloud Stream',
    sub: 'Live Streaming',
    icon: Cloud,
    details: ['Streaming mode', 'Meet device control', 'Status only'],
    color: 'red'
  }, {
    id: OperationMode.Standby,
    label: 'Standby Mode',
    sub: 'System Sleep',
    icon: Power,
    details: ['Displays off', 'Audio muted', 'Low power state'],
    color: 'gray'
  }];
  return <div className="h-full p-4 flex flex-col" data-id="element-217">
      <div className="mb-4" data-id="element-218">
        <h2 className="text-xl font-bold text-white mb-1 tracking-tight" data-id="element-219">
          Operation Modes
        </h2>
        <p className="text-sm text-gray-400" data-id="element-220">
          Select a mode to configure video, audio, and cameras automatically.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4 flex-1" data-id="element-221">
        {modes.map(mode => {
        const isActive = currentMode === mode.id;
        return <button key={mode.id} onClick={() => onModeSelect(mode.id)} className={`
                relative flex flex-col p-4 rounded-xl border-2 transition-all duration-300
                hover:scale-[1.02] active:scale-[0.98]
                ${isActive ? mode.id === OperationMode.Standby ? 'bg-gradient-to-br from-gray-700 to-gray-800 border-gray-500 shadow-[0_0_30px_rgba(107,114,128,0.4)] z-10' : 'bg-gradient-to-br from-blue-600 to-blue-700 border-blue-400 shadow-[0_0_30px_rgba(37,99,235,0.4)] z-10' : 'bg-neutral-800/80 border-neutral-700 hover:border-neutral-500 hover:bg-neutral-750 backdrop-blur-sm'}
              `} data-id="element-222">
              {/* Icon & Status Badge */}
              <div className="flex items-start justify-between mb-3" data-id="element-223">
                <div className={`
                  p-2.5 rounded-lg transition-all
                  ${isActive ? 'bg-white/20 text-white shadow-lg' : 'bg-neutral-900/80 text-gray-300'}
                `} data-id="element-224">
                  <mode.icon size={28} strokeWidth={2} data-id="element-225" />
                </div>

                {isActive && <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/20 backdrop-blur-sm" data-id="element-226">
                    <span className="animate-pulse h-1.5 w-1.5 rounded-full bg-white" data-id="element-227"></span>
                    <span className="text-[10px] font-bold text-white uppercase tracking-wider" data-id="element-228">
                      Active
                    </span>
                  </div>}
              </div>

              {/* Title & Subtitle */}
              <div className="mb-3" data-id="element-229">
                <h3 className={`text-base font-bold mb-0.5 tracking-tight leading-tight ${isActive ? 'text-white' : 'text-gray-100'}`} data-id="element-230">
                  {mode.label}
                </h3>
                <p className={`text-xs font-medium ${isActive ? mode.id === OperationMode.Standby ? 'text-gray-300' : 'text-blue-100' : 'text-gray-500'}`} data-id="element-231">
                  {mode.sub}
                </p>
              </div>

              {/* Details List */}
              <div className="space-y-1.5 mt-auto" data-id="element-232">
                {mode.details.map((detail, idx) => <div key={idx} className="flex items-start gap-1.5" data-id="element-233">
                    {isActive ? <CheckCircle2 size={12} className={mode.id === OperationMode.Standby ? 'text-gray-300' : 'text-blue-200'} mt={0.5} flexShrink={0} strokeWidth={2.5} data-id="element-234" /> : <Circle size={12} className="text-gray-600 mt-0.5 flex-shrink-0" strokeWidth={2} data-id="element-235" />}
                    <span className={`text-[11px] leading-tight ${isActive ? mode.id === OperationMode.Standby ? 'text-gray-300' : 'text-blue-100' : 'text-gray-500'}`} data-id="element-236">
                      {detail}
                    </span>
                  </div>)}
              </div>
            </button>;
      })}
      </div>

      {/* System Readiness Indicator */}
      <div className="mt-4 flex items-center justify-center gap-6 px-4 py-2.5 bg-neutral-800/50 rounded-lg border border-neutral-700" data-id="element-237">
        <div className="flex items-center gap-1.5" data-id="element-238">
          <MonitorPlay size={14} className="text-green-400" data-id="element-239" />
          <span className="text-xs text-gray-300" data-id="element-240">
            Video: <span className="text-green-400 font-bold" data-id="element-241">Ready</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-242">
          <Mic size={14} className="text-green-400" data-id="element-243" />
          <span className="text-xs text-gray-300" data-id="element-244">
            Audio: <span className="text-green-400 font-bold" data-id="element-245">Ready</span>
          </span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-246">
          <Camera size={14} className="text-green-400" data-id="element-247" />
          <span className="text-xs text-gray-300" data-id="element-248">
            Cameras: <span className="text-green-400 font-bold" data-id="element-249">Ready</span>
          </span>
        </div>
      </div>
    </div>;
}